import { useState, useEffect } from "react";
import { getAllEstudiantes } from "../../../services/estudiante.service";
import { getAllIdiomasByIdEstudiante } from "../../../services/idioma.service";
import { getAllRegistrosV2ByIdEstudianteIdIdiomaEstado } from "../../../services/registroV2.service";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { GraduationCap, Clock, ArrowLeft, ArrowRight } from "lucide-react";

export const HistorialNotasPage = () => {
  const [historialNotas, setHistorialNotas] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [idiomasEstudiante, setIdiomasEstudiante] = useState(null);
  const userName = useSelector((store) => store.usuario.username);
  const navigate = useNavigate();

  useEffect(() => {
    const obtenerIdiomas = async () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem("userInfoES") || "{}");
        const cacheKey = `estudianteIdByUser_${userName || userInfo?.username || ""}`;
        let estudianteId = JSON.parse(localStorage.getItem(cacheKey) || "null");

        if (!estudianteId) {
          const estudiantesData = await getAllEstudiantes();
          const estudiante = estudiantesData.find((est) => est.usuario.username === userName);
          if (estudiante?.estudiante?.idEstudiante) {
            estudianteId = estudiante.estudiante.idEstudiante;
            localStorage.setItem(cacheKey, JSON.stringify(estudianteId));
          } else {
            return;
          }
        }

        const TTL_MS = 5 * 60 * 1000;
        const idiomasCacheKey = `idiomasByEstudiante_${estudianteId}`;
        const cached = JSON.parse(localStorage.getItem(idiomasCacheKey) || "null");
        const now = Date.now();

        if (cached?.data && typeof cached.timestamp === "number") {
          setIdiomasEstudiante(cached.data);
        }

        if (!cached?.timestamp || now - cached.timestamp > TTL_MS) {
          const idiomasFresh = await getAllIdiomasByIdEstudiante(estudianteId);
          setIdiomasEstudiante(idiomasFresh);
          localStorage.setItem(
            idiomasCacheKey,
            JSON.stringify({ data: idiomasFresh, timestamp: now })
          );
        } else if (!cached?.data) {
          const idiomas = await getAllIdiomasByIdEstudiante(estudianteId);
          setIdiomasEstudiante(idiomas);
          localStorage.setItem(
            idiomasCacheKey,
            JSON.stringify({ data: idiomas, timestamp: now })
          );
        }
      } catch (err) {
        console.log(err);
      }
    };

    if (userName) {
      obtenerIdiomas();
    }
  }, [userName]);

  useEffect(() => {
    if (idiomasEstudiante && idiomasEstudiante.length > 0 && userName) {
      obtenerHistorialNotas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idiomasEstudiante]);

  const obtenerHistorialNotas = async () => {
    setCargandoHistorial(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem("userInfoES") || "{}");
      const cacheKey = `estudianteIdByUser_${userName || userInfo?.username || ""}`;
      let estudianteId = JSON.parse(localStorage.getItem(cacheKey) || "null");

      if (!estudianteId) {
        const estudiantesData = await getAllEstudiantes();
        const estudiante = estudiantesData.find((est) => est.usuario.username === userName);
        if (estudiante?.estudiante?.idEstudiante) {
          estudianteId = estudiante.estudiante.idEstudiante;
          localStorage.setItem(cacheKey, JSON.stringify(estudianteId));
        } else {
          setCargandoHistorial(false);
          return;
        }
      }
      if (idiomasEstudiante && idiomasEstudiante.length > 0) {
        const estados = ["Aprobado", "Reprobado", "Culminado", "Progreso"];
        const registrosPromises = [];

        for (const idioma of idiomasEstudiante) {
          for (const estado of estados) {
            registrosPromises.push(
              getAllRegistrosV2ByIdEstudianteIdIdiomaEstado(
                estudianteId,
                idioma.idIdioma,
                estado
              ).catch(() => [])
            );
          }
        }

        const resultados = await Promise.all(registrosPromises);
        const todosRegistros = resultados.flat().filter(reg => reg !== null && reg !== undefined);
        const registrosOrdenados = todosRegistros
          .sort((a, b) => {
            const fechaA = new Date(a.creadoEn || a.grupo?.creadoEn || 0);
            const fechaB = new Date(b.creadoEn || b.grupo?.creadoEn || 0);
            return fechaB - fechaA;
          });

        setHistorialNotas(registrosOrdenados);
      }
    } catch (err) {
      console.log("Error obteniendo historial de notas:", err);
    } finally {
      setCargandoHistorial(false);
    }
  };

  const formatearNota = (nota) => {
    if (nota === null || nota === undefined || nota === "") return "-";
    return parseFloat(nota).toFixed(1);
  };

  const obtenerEstadoColor = (estado) => {
    switch (estado) {
      case 'Aprobado':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Reprobado':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'Culminado':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    }
  };

  return (
    <div className="bg-white min-h-screen w-full">
      <ContenedorPageTemplate fondo={false}>
        <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-theme-blue" />
            </button>
            <div className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-theme-blue" />
              <h1 className="text-3xl md:text-4xl font-bold text-theme-blue">
                Historial de Notas
              </h1>
            </div>
          </div>
        </div>

        {/* Contenido */}
        {cargandoHistorial ? (
          <div className="text-center py-16">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-spin" />
            <p className="text-gray-500 text-lg">Cargando historial...</p>
          </div>
        ) : historialNotas.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <div className="space-y-4">
              {historialNotas.map((registro) => (
                <Link
                  key={registro.idRegistroNotasV2}
                  to={`/modulos/${registro.grupo?.modulo?.idioma?.idIdioma || registro.idioma?.idIdioma}/detalles`}
                  state={{ registro }}
                  className="block bg-white hover:bg-blue-50 rounded-xl p-5 border-2 border-gray-200 hover:border-theme-blue/50 transition-all duration-300 shadow-sm hover:shadow-md"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg md:text-xl font-bold text-gray-800">
                          {registro.grupo?.modulo?.nombre || "Sin módulo"}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${obtenerEstadoColor(registro.estadoV2 || registro.estado)}`}>
                          {registro.estadoV2 || registro.estado || "Sin estado"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <span className="font-semibold">Grupo:</span> {registro.grupo?.nombre || "N/A"}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="font-semibold">Idioma:</span> {registro.grupo?.modulo?.idioma?.nombre || registro.idioma?.nombre || "N/A"}
                        </span>
                        {registro.grupo?.docente && (
                          <span className="flex items-center gap-1">
                            <span className="font-semibold">Docente:</span> {registro.grupo.docente.nombre} {registro.grupo.docente.apellido}
                          </span>
                        )}
                        {registro.nivelModulo && (
                          <span className="flex items-center gap-1">
                            <span className="font-semibold">Nivel:</span> {registro.nivelModulo}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {registro.notaFinal !== null && registro.notaFinal !== undefined && (
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-1">Nota Final</p>
                          <p className="text-2xl md:text-3xl font-bold text-theme-blue">
                            {formatearNota(registro.notaFinal)}
                          </p>
                        </div>
                      )}
                      <ArrowRight className="w-6 h-6 text-theme-blue flex-shrink-0" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <GraduationCap className="w-24 h-24 text-gray-300 mx-auto mb-6" />
            <p className="text-gray-500 text-xl">Aún no tienes notas registradas</p>
            <button
              onClick={() => navigate("/inicio")}
              className="mt-6 bg-gradient-to-r from-theme-blue to-blue-600 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center gap-2 mx-auto"
            >
              <ArrowLeft className="w-5 h-5" />
              Volver al inicio
            </button>
          </div>
        )}
        </div>
      </ContenedorPageTemplate>
    </div>
  );
};

export default HistorialNotasPage;

